// Library Methods
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

// Utils
import { asPercentage, getDecimalNum } from "src/utils/number/numberHelper";

// Constants
import { gridTypeEnums } from "src/constants/enums";

type ValueLegendType = {
  isTerminated: boolean;
  examReport: ExamSectionReportType;
};

const ValueLegend = ({ isTerminated, examReport }: ValueLegendType) => {
  const { t } = useTranslation();

  const is24_2C = useMemo(
    () => examReport?.exam?.grid === gridTypeEnums.G_24_2_C.type,
    [examReport?.exam?.grid]
  );

  const GHT = {
    Undefined: "Undefined",
    AbnormallyHighSensitivity: "AbnormallyHighSensitivity",
    OutsideNormalLimits: "OutsideNormalLimits",
    Borderline: "Borderline",
    GeneralReductionOfSensitivity: "GeneralReductionOfSensitivity",
    WithinNormalLimits: "WithinNormalLimits",
  };

  const ghtColor = {
    [GHT.Undefined]: "#000",
    [GHT.AbnormallyHighSensitivity]: "red",
    [GHT.OutsideNormalLimits]: "red",
    [GHT.Borderline]: "#E2772E",
    [GHT.GeneralReductionOfSensitivity]: "teal",
    [GHT.WithinNormalLimits]: "green",
  };

  const totalMeanDeviation = useMemo(
    () => examReport?.postProcessing?.totalMeanDeviation,
    [examReport?.postProcessing?.totalMeanDeviation]
  );
  const patternStandardDeviation = useMemo(
    () => examReport?.postProcessing?.patternStandardDeviation,
    [examReport?.postProcessing?.patternStandardDeviation]
  );
  const retinaLogikFieldIndex = useMemo(
    () => examReport?.postProcessing?.retinaLogikFieldIndex,
    [examReport?.postProcessing?.retinaLogikFieldIndex]
  );
  const glaucomaHemifieldTestIndex = useMemo(
    () => examReport?.postProcessing?.glaucomaHemifieldTestIndex,
    [examReport?.postProcessing?.glaucomaHemifieldTestIndex]
  );

  const formattedTotalMD = useMemo(() => {
    let returnVal = 0;
    if (totalMeanDeviation) {
      returnVal =
        totalMeanDeviation > 0
          ? totalMeanDeviation % 3
          : totalMeanDeviation > -0.01
            ? 0
            : totalMeanDeviation;
    }
    return getDecimalNum(returnVal, 2);
  }, [totalMeanDeviation]);

  const formattedPSD = useMemo(() => {
    let returnVal = 0;
    if (patternStandardDeviation) {
      returnVal =
        patternStandardDeviation < 0 && patternStandardDeviation > -0.01
          ? 0
          : patternStandardDeviation;
    }
    return getDecimalNum(returnVal, 2);
  }, [patternStandardDeviation]);

  return (
    <Box display="flex" flexDirection="column" width="100%">
      {glaucomaHemifieldTestIndex !== undefined && (
        <Typography
          fontSize={{ xs: "14px", sm: "12px" }}
          color={`${ghtColor[glaucomaHemifieldTestIndex]}`}
        >
          <span style={{ color: "#000" }}>GHT{is24_2C && "-24"}: </span>
          {isTerminated ? "-" : `${t(glaucomaHemifieldTestIndex)}`}
        </Typography>
      )}
      {retinaLogikFieldIndex !== undefined && (
        <Typography fontSize={{ xs: "14px", sm: "12px" }} noWrap>
          VRFI{is24_2C && "-24"}:{" "}
          {isTerminated ? "-" : asPercentage(retinaLogikFieldIndex, 0)}
        </Typography>
      )}
      <Typography fontSize={{ xs: "14px", sm: "12px" }} noWrap>
        MD: {isTerminated ? "-" : formattedTotalMD}
      </Typography>
      <Typography fontSize={{ xs: "14px", sm: "12px" }} noWrap>
        PSD: {isTerminated ? "-" : formattedPSD}
      </Typography>
    </Box>
  );
};

export default ValueLegend;
